import Sample from 'Sample.js';
import Group from './Group.js';
import {groupFactory} from './GroupFactory.js';
import {
    setEffectParameterValues,
    setGroupTracks,
} from 'actions/index.js';
import {uuid} from 'uuidv4';
import {getVolume} from 'utils/getVolume.js';
import Tone from 'tone';
import {getEffect} from 'effects/EffectsList.js';
import GroupVolumeChangeEvent from 'session/GroupVolumeChangeEvent.js';
import GroupMuteEvent from 'session/GroupMuteEvent.js';
import ParameterContext from 'effects/ParameterContext.js';
import EffectsChain from 'effects/EffectsChain.js';
import {setGroupVolume,
        setGroupMuted,
        setGroupPanning} from 'actions/index.js';
import {SAMPLER, INSTRUMENT, MIDI, AUDIO} from 'constants/track-types.js';

let counter = 0;

const MUTED_VOLUME = -125;
const UNMUTED_VOLUME = 0;

class Groups {
    constructor(channelMixer, sends, sequencerEngine, subSequence) {
        this.groups = [];
        this.subSequence = subSequence;
        this.channelMixer = channelMixer;
        this.sends = sends;
        this.sequencerEngine = sequencerEngine;
        this.doneLoading = false;
    }

    startSendingLevels() {
        setInterval(() => {
            if (!this.zObject) {
                return;
            }
            let levels = this.groups.map(
                group => {
                    let x = group.meter.getLevel();
                    return x < -80 ? 0 : Math.pow((x + 80)/86, 2);
                });
            this.zObject.sendMessage("group levels", levels);
        }, 20);
    }

    sendVolumes() {
        if (this.zObject) {
            this.zObject.sendLevels();
        }
    }

    deleteGroup(group) {
        group.tracks.forEach(
            t => t.dispose());
        group.dispose();

        let i = this.groups.indexOf(group);
        this.groups.splice(i, 1);
        this.updateGroupNumbers();
    }

    updateGroupNumbers() {
        for (let i=0; i < this.groups.length; i++) {
            this.groups[i].groupNumber = i;
        }
    }

    newGroup(track) {
        let group = groupFactory.useGroup(
            [],
            this.channelMixer,
            this.groups.length,
            this.sequencerEngine,
            this.subSequence);
        this.groups.push(group);
        group.connectAudio(this.getMaxChannel());

        if (track) {
            this.addToGroup(track, group);
        }
        return group;
    }

    async fromJson(json, sequencerEngine) {
        return new Promise(async (resolve, reject) => {
            if (json === undefined) {
                this.sequencerEngine.tracks.forEach(
                    track => this.addTrack(track));
                resolve();
                return;
            }
            for (let i=0; i < json.length; i++) {
                let group = json[i];
                let loadedGroup = await (groupFactory.useGroup(
                    [],
                    this.channelMixer,
                    this.groups.length,
                    this.sequencerEngine,
                    this.subSequence))
                    .fromJson(group, sequencerEngine);

                if (loadedGroup.tracks.length > 0) {
                    loadedGroup.connectAudio(this.getMaxChannel());
                    this.groups.push(loadedGroup);
                }

                if (group.outputs) {
                    let [left, right] = group.outputs;
                    if (this.channelMixer.switchChannelOutput(
                        loadedGroup.outputNode, 
                        left,
                        right)) {
                        loadedGroup.outputs = [left, right];
                    }
                }
            }

            resolve();
        });
    }

    updateGroups() {
        this.groups.forEach(group => group.setVolume(group.volume));
        this.groups.forEach(group => group.setPanning(group.panning));
    }

    addToGroup(trackToAdd, group) {
        let oldGroup = trackToAdd.group;
        let shouldDelete = oldGroup.tracks.length == 1;
        let index = this.groups.indexOf(oldGroup);
        trackToAdd.disconnectFromGroup();

        group.addTrack(trackToAdd);
        group.tags = Array.from(new Set(group.tags.concat(oldGroup.tags)));
        if (shouldDelete) {
            this.groups.splice(index, 1);
            oldGroup.dispose();
            this.groups.forEach(
                (group, groupNumber) => group.groupNumber = groupNumber);
        } else {
            index = oldGroup.tracks.indexOf(trackToAdd);
            oldGroup.tracks.splice(index, 1);
        }

        group.updateRedux();
        oldGroup.updateRedux();
    }

    deleteTrack(trackToDelete) {
        let group = trackToDelete.group;
        if (group === undefined || group === null) {
            return;
        }
        let shouldDelete = group.tracks.length == 1;
        let index = this.groups.indexOf(group);
        trackToDelete.disconnectFromGroup();

        if (shouldDelete) {
            // since there are no tracks left we must dispose
            this.groups.splice(index, 1);
            group.dispose();
        } else {
            index = group.tracks.indexOf(trackToDelete);
            group.tracks.splice(index, 1);
        }
    }

    async addTrack(track) {
        switch (track.getTrackType()) {
        case SAMPLER:
            let tags = track.sample.tags;
            if (tags === undefined) {
                tags = await track.sample.fetchTags();
            }
            this.resolveTrackTags(track, tags);
            break;
        default:
            this.resolveTrackTags(track, [track.getTrackType()]);
        }
    }

    addMidiTrack(track) {
        this.resolveTrackTags(track, ['Midi Tracks']);
    }

    resolveTrackTags(track, tags) {
        // check if theres a group that contains any of the tags
        // and get group with most intersections
        let max = 0;
        let resolvedGroup = null;
        if (this.groups.length >= 4) {
            resolvedGroup = this.groups[Math.floor(Math.random()*4)];
        } else {
            for (let i in this.groups) {
                let group = this.groups[i];
                let intersection = intersect(group.tags, tags);
                if (intersection.length > max) {
                    resolvedGroup = group;
                    max = intersection.length;
                }
            }
        }

            /*
        if (this.groups.length > 0) {
            resolvedGroup = this.groups[0];
        }
        */

        // theres none that intersect so create a new group
        if (resolvedGroup == null) {
            let group = this.newGroup();
            group.addTrack(track);
            /*
            let group = groupFactory.useGroup(
                tags,
                this.channelMixer,
                this.groups.length,
                this.sequencerEngine,
                this.subSequence);
            group.connectAudio(this.getMaxChannel());
            console.log('creating new group');
            group.addTrack(track);
            this.groups.push(group);
            */
            return;
        }

        // otherwise use the resolved group we found 
        resolvedGroup.tags = intersect(resolvedGroup.tags, tags);
        resolvedGroup.addTrack(track);
    }

    dispose() {
        this.groups.forEach(group => groupFactory.releaseGroup(group));
        this.groups = undefined;
    }

    getJson(sequencerEngine) {
        return this.groups.map(
            group => group.getJson(sequencerEngine));
    }

    // returns the max ouput channel a group is connected to
    // used so ChannelMixer.js can use the next availabe output
    getMaxChannel() {
        let channels = []
        this.groups.map(group => group.outputs)
            .filter(outputs => outputs !== undefined)
            .forEach(outputs => channels = channels.concat(outputs));

        this.sends.sends.map(send => send.outputs)
            .filter(outputs => outputs !== undefined)
            .forEach(outputs => channels = channels.concat(outputs));


        let sortedChannels = channels.sort((x, y) => x - y).reverse();

        if (sortedChannels.length === 0) {
            return -1;
        }

        let max = sortedChannels[0];

        for (let i=0; i < max; i+=2) {
            if (sortedChannels.indexOf(i) == -1 &&
                sortedChannels.indexOf(i + 1) == -1) {
                return i - 1;
            }
        }

        return max;
    }
}

function intersect(array1, array2) {
    return array1.filter(value => -1 !== array2.indexOf(value));
}

function isNumber(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
}

export default Groups;
